/**
 * Sidebar — Pattern A navigation sidebar.
 * w-52, 3 nav groups (CORE/AUTOMATION/SYSTEM), company switcher,
 * credits badge, user footer. Collapses to hamburger on mobile.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import { nav, sidebar, colors, creditsColor } from '../../theme';
import {
  MessageSquare,
  Database,
  BarChart3,
  Globe,
  Zap,
  Clock,
  Activity,
  FileText,
  Settings,
  Shield,
  LifeBuoy,
  ChevronDown,
  LogOut,
  Users,
  Menu,
  X,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────

export interface SidebarNavItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  badge?: string | number;
  /** If set, item is only visible when user has this role */
  visibleRoles?: UserRole[];
}

export interface NavGroup {
  label: string;
  items: SidebarNavItem[];
}

export type UserRole = 'super_admin' | 'admin' | 'manager' | 'viewer';

export interface Company {
  id: string;
  name: string;
}

export interface SidebarProps {
  /** Navigation groups (CORE, AUTOMATION, SYSTEM) */
  navGroups: NavGroup[];
  /** Currently active route key (e.g. 'chat', 'data', 'dashboard') */
  activePath: string;
  /** Navigation handler */
  onNavigate: (href: string) => void;
  /** Current user role for visibility filtering */
  userRole: UserRole;
  /** Credits balance */
  creditsBalance: number;
  /** List of companies for company switcher */
  companies: Company[];
  /** Currently selected company */
  activeCompanyId: string;
  /** Company change handler */
  onCompanyChange: (companyId: string) => void;
  /** User display name */
  userName?: string;
  /** User email */
  userEmail?: string;
  /** User avatar URL */
  userAvatar?: string;
  /** Logout handler */
  onLogout?: () => void;
  /** Mobile overlay state */
  mobileOpen?: boolean;
  /** Mobile overlay toggle */
  onMobileToggle?: () => void;
  /** Custom class */
  className?: string;
}

// ─── Default Nav Groups ────────────────────────────────────────────

export const DEFAULT_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Core',
    items: [
      { label: 'Chat', href: '/chat', icon: <MessageSquare className="h-4 w-4" /> },
      { label: 'Data', href: '/data', icon: <Database className="h-4 w-4" /> },
      { label: 'Dashboard', href: '/dashboard', icon: <BarChart3 className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Automation',
    items: [
      { label: 'Connectors', href: '/connectors', icon: <Globe className="h-4 w-4" /> },
      { label: 'Composio', href: '/composio', icon: <Zap className="h-4 w-4" /> },
      { label: 'Reminders', href: '/reminders', icon: <Clock className="h-4 w-4" /> },
      { label: 'Evolution', href: '/evolution', icon: <Activity className="h-4 w-4" /> },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Audit', href: '/audit', icon: <FileText className="h-4 w-4" /> },
      { label: 'Settings', href: '/settings', icon: <Settings className="h-4 w-4" /> },
      { label: 'Support', href: '/support', icon: <LifeBuoy className="h-4 w-4" /> },
      { label: 'Admin', href: '/admin', icon: <Shield className="h-4 w-4" />, visibleRoles: ['super_admin'] },
    ],
  },
];

// ─── Component ─────────────────────────────────────────────────────

export function Sidebar({
  navGroups,
  activePath,
  onNavigate,
  userRole,
  creditsBalance,
  companies,
  activeCompanyId,
  onCompanyChange,
  userName,
  userEmail,
  userAvatar,
  onLogout,
  mobileOpen = false,
  onMobileToggle,
  className,
}: SidebarProps) {
  const [companySwitcherOpen, setCompanySwitcherOpen] = React.useState(false);
  const companySwitcherRef = React.useRef<HTMLDivElement>(null);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);

  // Close company switcher on outside click
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (companySwitcherRef.current && !companySwitcherRef.current.contains(e.target as Node)) {
        setCompanySwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sidebarContent = (
    <div className={cn(
      'flex h-full flex-col',
      sidebar.width,
      sidebar.bg,
      sidebar.border,
      'p-4',
      className,
    )}>
      {/* Logo */}
      <div className="mb-6">
        <button
          onClick={() => onNavigate('/chat')}
          className="text-lg font-bold text-slate-100 hover:opacity-80 transition-opacity"
        >
          Axon<span className={colors.text.accent}>BOS</span>
        </button>
        <p className="text-[10px] text-slate-500 uppercase tracking-widest">
          Business Operating System
        </p>
      </div>

      {/* Company Switcher + Credits */}
      <div ref={companySwitcherRef} className="relative mb-4">
        <button
          onClick={() => setCompanySwitcherOpen(!companySwitcherOpen)}
          className={cn(
            'w-full px-2 py-2 rounded text-xs text-left',
            colors.bg.hover,
          )}
        >
          <div className={colors.text.secondary}>Company</div>
          <div className="flex items-center justify-between">
            <span className="text-slate-200 font-medium truncate">
              {activeCompany?.name ?? 'Select Company'}
            </span>
            <ChevronDown className={cn(
              'h-3 w-3 text-slate-500 transition-transform',
              companySwitcherOpen && 'rotate-180',
            )} />
          </div>
          <div className={cn('mt-1', creditsColor(creditsBalance))}>
            Credits: ₹{creditsBalance.toFixed(2)}
          </div>
        </button>

        {/* Dropdown */}
        {companySwitcherOpen && (
          <div className={cn(
            'absolute left-0 right-0 top-full z-50 mt-1 rounded border',
            colors.bg.card,
            colors.border.default,
            'shadow-lg',
          )}>
            {companies.map((company) => (
              <button
                key={company.id}
                onClick={() => {
                  onCompanyChange(company.id);
                  setCompanySwitcherOpen(false);
                }}
                className={cn(
                  'w-full px-3 py-2 text-left text-xs transition-colors',
                  company.id === activeCompanyId
                    ? cn(colors.accent.primaryGhost, colors.text.accent)
                    : cn(colors.text.secondary, 'hover:bg-slate-700/50'),
                )}
              >
                {company.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation Groups */}
      <nav className="flex-1 space-y-4 text-sm overflow-y-auto">
        {navGroups.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (!item.visibleRoles) return true;
            return item.visibleRoles.includes(userRole);
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={group.label}>
              <div className={cn(nav.groupLabel, 'mb-1')}>
                {group.label}
              </div>
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const isActive =
                    activePath === item.href ||
                    activePath.startsWith(item.href + '/');

                  return (
                    <button
                      key={item.href}
                      onClick={() => {
                        onNavigate(item.href);
                        onMobileToggle?.();
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded text-left transition-colors',
                        isActive ? nav.active : nav.inactive,
                      )}
                    >
                      {item.icon}
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== undefined && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-300">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User Footer */}
      <div className="mt-auto pt-4 border-t border-slate-800 text-xs">
        <div className="flex items-center gap-2 mb-3">
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={userName ?? 'User'}
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 flex-shrink-0">
              <Users className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0">
            {userName && (
              <p className="text-slate-300 font-medium truncate">{userName}</p>
            )}
            {userEmail && (
              <p className="text-slate-500 truncate">{userEmail}</p>
            )}
          </div>
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs transition-colors',
              colors.bg.card,
              'hover:bg-slate-700 text-slate-300',
            )}
          >
            <LogOut className="h-3 w-3" />
            Sign Out
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger button (rendered by parent, but this is the overlay) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className={cn('absolute inset-0', colors.bg.overlay)}
            onClick={onMobileToggle}
          />
          {/* Sidebar drawer */}
          <aside className="relative z-50 h-full">
            <button
              onClick={onMobileToggle}
              className="absolute top-4 right-[-44px] z-50 p-2 rounded bg-slate-800 text-slate-300"
            >
              <X className="h-5 w-5" />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:block min-h-screen flex-shrink-0">
        {sidebarContent}
      </aside>
    </>
  );
}

/** Hamburger trigger for mobile — place in the top bar */
export function SidebarMobileTrigger({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn('md:hidden p-2 rounded text-slate-300 hover:bg-slate-800', className)}
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
